'use client';

import { useState } from 'react';
import { Button, Text } from 'rizzui';
import { PiHeartBold, PiHeartFill } from 'react-icons/pi';
import { likePublicArticle } from '@/server/api';

export function ArticleLikeButton({
  articleSlug,
  initialLikes,
  buttonClassName,
}: {
  articleSlug: string;
  initialLikes: number;
  buttonClassName?: string;
}) {
  const [likesCount, setLikesCount] = useState(initialLikes);
  const [isLiking, setIsLiking] = useState(false);
  const [hasLiked, setHasLiked] = useState(false);
  const [error, setError] = useState('');

  const handleLike = async () => {
    if (isLiking || hasLiked) return;

    setIsLiking(true);
    setError('');

    try {
      const nextLikesCount = await likePublicArticle(articleSlug);
      setLikesCount((current) => nextLikesCount ?? current + 1);
      setHasLiked(true);
    } catch {
      setError('Like could not be added. Please try again.');
    } finally {
      setIsLiking(false);
    }
  };

  return (
    <div className="relative flex items-center ">
      <Button
        type="button"
        variant="outline"
        rounded="pill"
        aria-label={
          hasLiked
            ? `Liked · ${likesCount} likes`
            : `Like article · ${likesCount} likes`
        }
        disabled={hasLiked}
        isLoading={isLiking}
        onClick={handleLike}
        className={`border-black/10 bg-transparent px-4 text-[#07182f] hover:border-[#0183d0] hover:bg-[#edf7ff] hover:text-[#0183d0] dark:border-white/20 dark:text-white dark:hover:bg-white/10 ${buttonClassName ?? ''}`}
      >
        {hasLiked ? (
          <PiHeartFill className="mr-2.5 h-4 w-4 text-[#0183d0]" />
        ) : (
          <PiHeartBold className="mr-2.5 h-4 w-4" />
        )}
        {likesCount} {likesCount === 1 ? 'like' : 'likes'}
      </Button>
      <Text
        aria-live="polite"
        className="absolute left-0 top-full mt-1 min-w-max text-xs text-red-600"
      >
        {error}
      </Text>
    </div>
  );
}
